declare const __RASANA_RELEASE_ID__:string;

export const CURRENT_RELEASE_ID=__RASANA_RELEASE_ID__;

export type SupportAccess={
 grantId:string;
 expiresAt:string;
 reason:string;
 profile:string;
};

export type Session={
 token:string;
 user:{id:string;name:string;email:string};
 organisation:{id:string;name:string;slug:string};
 roles:string[];
 permissions:string[];
 permissionScopes?:Record<string,string>;
 dataScope:string;
 kind:'organisation'|'platform';
 enabledModules?:string[];
 workspaceSlug?:string;
 entitlements?:Record<string,unknown>;
 limits?:Record<string,unknown>;
 subscription?:{
  planCode:string;
  planName:string;
  status:string;
  supportLevel:string;
  readOnly:boolean;
 };
 onboardingAccess?:{
  expiresAt:string;
  phase:string;
 };
 supportAccess?:SupportAccess;
 releaseId?:string;
};

export type ServiceReadiness={
 name:string;
 status:
  |'ready'
  |'starting'
  |'unavailable'
  |'unconfigured'
  |'timeout';
 httpStatus?:number;
 latencyMs:number;
 error?:string;
};

export type Readiness={
 status:'ok'|'degraded';
 authenticationReady:boolean;
 platformAuthenticationReady?:boolean;
 readyCount:number;
 total:number;
 timestamp:string;
 checks:ServiceReadiness[];
};

const rawApi=import.meta.env.VITE_API_URL||'http://localhost:3000';
const apiBase=(/^https?:\/\//i.test(rawApi)?rawApi:`https://${rawApi}`).replace(/\/$/,'');
const transientStatuses=new Set([502,503,504]);
const transientNetworkCodes=new Set([
 'ECONNRESET',
 'ETIMEDOUT',
 'ECONNREFUSED',
 'UND_ERR_SOCKET',
 'UND_ERR_CONNECT_TIMEOUT'
]);
const SESSION_KEY='rasana.session';
const PLATFORM_RETURN_KEY='rasana.platform.return-session';

function sleep(ms:number){
 return new Promise(resolve=>setTimeout(resolve,ms));
}

function transientNetworkError(error:unknown){
 const code=
  (error as any)?.cause?.code??
  (error as any)?.code;

 return transientNetworkCodes.has(
  String(code??'')
 );
}

function clearStoredSessions(){
 sessionStorage.removeItem(SESSION_KEY);
 sessionStorage.removeItem(PLATFORM_RETURN_KEY);
}

export function getSession():Session|null{
 try{
  const parsed=JSON.parse(sessionStorage.getItem(SESSION_KEY)||'null');
  if(!parsed)return null;

  if(
   String(parsed.releaseId??'')!==
   CURRENT_RELEASE_ID
  ){
   clearStoredSessions();
   return null;
  }

  return{
   ...parsed,
   roles:Array.isArray(parsed.roles)?parsed.roles:[],
   permissions:Array.isArray(parsed.permissions)?parsed.permissions:[],
   permissionScopes:
    parsed.permissionScopes&&typeof parsed.permissionScopes==='object'
     ?parsed.permissionScopes
     :{},
   enabledModules:Array.isArray(parsed.enabledModules)
    ?parsed.enabledModules
    :undefined,
   kind:parsed.kind==='platform'?'platform':'organisation',
   releaseId:CURRENT_RELEASE_ID
  }as Session;
 }catch{
  clearStoredSessions();
  return null;
 }
}

export function setSession(v:Session|null){
 if(v){
  sessionStorage.setItem(
   SESSION_KEY,
   JSON.stringify({
    ...v,
    releaseId:CURRENT_RELEASE_ID
   })
  );
 }else{
  sessionStorage.removeItem(SESSION_KEY);
 }
}

export function expireSessionForRelease(){
 clearStoredSessions();
}

export function savePlatformReturnSession(session:Session){
 sessionStorage.setItem(
  PLATFORM_RETURN_KEY,
  JSON.stringify({
   ...session,
   releaseId:CURRENT_RELEASE_ID
  })
 );
}

export function takePlatformReturnSession():Session|null{
 try{
  const raw=sessionStorage.getItem(PLATFORM_RETURN_KEY);
  sessionStorage.removeItem(PLATFORM_RETURN_KEY);

  if(!raw)return null;

  const parsed=JSON.parse(raw)as Session;

  if(
   String(parsed.releaseId??'')!==
   CURRENT_RELEASE_ID
  ){
   return null;
  }

  return{
   ...parsed,
   releaseId:CURRENT_RELEASE_ID
  };
 }catch{
  sessionStorage.removeItem(PLATFORM_RETURN_KEY);
  return null;
 }
}

export function clearPlatformReturnSession(){
 sessionStorage.removeItem(PLATFORM_RETURN_KEY);
}

export function can(code:string){
 const p=getSession()?.permissions??[];
 return p.includes('*')||
  p.includes(code)||
  p.includes(code.split('.')[0]+'.*');
}

export function canModule(module:string){
 const modules=getSession()?.enabledModules;
 return !Array.isArray(modules)||modules.includes(module);
}

export function hasEntitlement(key:string){
 const value=getSession()?.entitlements?.[key];
 return value===true||value==='standard'||value==='configurable'||value==='full';
}

export function subscriptionLimit(key:string){
 const value=getSession()?.limits?.[key];
 const n=Number(value);
 return Number.isFinite(n)?n:null;
}

function workspaceSlugFromBrowser(){
 try{
  const host=window.location.hostname.toLowerCase();
  const configured=String(import.meta.env.VITE_RASANA_BASE_DOMAIN||'rasana.com')
   .trim().toLowerCase();
  const reserved=new Set([
   'www','app','api','platform','admin','support','status','login','auth',
   'billing','help','docs'
  ]);
  if(!host.endsWith('.'+configured))return null;
  const slug=host.slice(0,-1*(configured.length+1)).split('.').pop()??'';
  return slug&&!reserved.has(slug)?slug:null;
 }catch{return null;}
}

export async function api<T=any>(
 path:string,
 init:RequestInit={}
):Promise<T>{
 const s=getSession();
 const headers=new Headers(init.headers);

 headers.set('content-type','application/json');

 const workspaceSlug=workspaceSlugFromBrowser();
 if(workspaceSlug){
  headers.set('x-rasana-workspace-slug',workspaceSlug);
 }

 if(s?.token){
  headers.set(
   'authorization',
   `Bearer ${s.token}`
  );
 }

 const method=(init.method??'GET').toUpperCase();

 const retryable=
  method==='GET'||
  method==='HEAD';

 const attempts=retryable?4:1;

 for(
  let attempt=1;
  attempt<=attempts;
  attempt++
 ){
  try{
   const r=await fetch(
    apiBase+path,
    {
     ...init,
     headers
    }
   );

   const text=await r.text();
   let data:any;

   try{
    data=text
     ?JSON.parse(text)
     :{};
   }catch{
    data={detail:text};
   }

   if(r.ok){
    return data as T;
   }

   const retry=
    retryable&&
    transientStatuses.has(r.status)&&
    attempt<attempts;

   if(retry){
    await sleep(
     attempt*1000
    );
    continue;
   }

   throw new Error(
    data.detail||
    data.message||
    `HTTP ${r.status}`
   );
  }catch(error){
   const retry=
    retryable&&
    transientNetworkError(error)&&
    attempt<attempts;

   if(retry){
    await sleep(
     attempt*1000
    );
    continue;
   }

   throw error;
  }
 }

 throw new Error(
  'Request failed after retry attempts.'
 );
}

export const post=<T=any>(path:string,body:any)=>api<T>(
 path,{method:'POST',body:JSON.stringify(body)}
);

export const patch=<T=any>(path:string,body:any)=>api<T>(
 path,{method:'PATCH',body:JSON.stringify(body)}
);

export const del=<T=any>(path:string)=>api<T>(
 path,{method:'DELETE'}
);
